/**
 * Tab Redesign Prototype
 * Chrome-like tab interface with improved visual connection and pinned tabs
 *
 * Access: http://flownotes.localhost/?test=tab-redesign
 *
 * Features:
 * - Chrome-like tab bar (darker background)
 * - Active tab connects to content (no border separator)
 * - Improved pinned tab indicator (accent dot)
 * - Fully draggable tabs
 * - Light/dark mode support
 */

import { useState, useRef, useEffect } from 'react'
import React from 'react'
import { X, FileText, FileCode, Mic, Network, Moon, Sun, Folder } from 'lucide-react'
import { BasecampIcon } from '@/components/icons/basecamp-icon'
import { cn } from '@/lib/utils'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  useDroppable,
} from '@dnd-kit/core'
import type { DragEndEvent, DragStartEvent, DragOverEvent } from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable'
import {
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Button } from './ui/button'
import { ContextMenu } from './features/context-menu'

type TabType = "work-spec" | "note" | "voice-memo" | "explorer" | "integration-basecamp" | "graph-view" | "integration-basecamp-todo" | "integration-voice-memos"

interface MockTab {
  id: string
  title: string
  type: TabType
  isPinned?: boolean
  isDirty?: boolean
}

const MOCK_TABS: MockTab[] = [
  { id: 'explorer-1', title: 'Explorer', type: 'explorer' },
  { id: 'tab-1', title: 'Athena Marketing', type: 'note', isPinned: true },
  { id: 'tab-2', title: 'Adding an FAQ page...', type: 'work-spec', isDirty: true },
  { id: 'tab-3', title: 'GGIA Recommendation Engine', type: 'integration-basecamp', isPinned: true },
  { id: 'tab-4', title: 'Voice Memo - Project Ideas', type: 'voice-memo' },
  { id: 'tab-5', title: 'Voice Memo Manager', type: 'integration-voice-memos' },
  { id: 'tab-6', title: 'Graph View', type: 'graph-view' },
]

// Helper function to get icon for tab type (used in both tab component and drag overlay)
const getIcon = (type: TabType) => {
  switch (type) {
    case "work-spec":
      return <FileCode className="h-3 w-3 text-blue-500" />
    case "voice-memo":
      return <Mic className="h-3 w-3 text-purple-500" />
    case "integration-basecamp":
      return <BasecampIcon className="h-3 w-3.5" />
    case "integration-voice-memos":
      return <Mic className="h-3 w-3 text-purple-500" />
    case "graph-view":
      return <Network className="h-3 w-3 text-muted-foreground" />
    case "explorer":
      return <Folder className="h-3 w-3 text-muted-foreground" />
    default:
      return <FileText className="h-3 w-3 text-muted-foreground" />
  }
}

function SortableTabPrototype({
  tab,
  isActive,
  onTabClick,
  onTabClose,
  onTabPinToggle,
  isDragging,
  isDroppingOnChat,
  activeTabId,
}: {
  tab: MockTab
  isActive: boolean
  onTabClick: (tabId: string) => void
  onTabClose: (tabId: string) => void
  onTabPinToggle: (tabId: string) => void
  isDragging: boolean
  isDroppingOnChat: boolean
  activeTabId: string | null
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: tab.id })

  const tabElementRef = useRef<HTMLDivElement>(null)

  // Hide tab during return animation when dropping on chat, then fade it back in
  const isThisTabDroppingOnChat = isDroppingOnChat && activeTabId === tab.id
  const [isHidingForAnimation, setIsHidingForAnimation] = useState(false)

  // Hide the tab immediately when dropping on chat, then fade it back in
  useEffect(() => {
    if (isThisTabDroppingOnChat) {
      // Hide immediately (no transition for hiding)
      setIsHidingForAnimation(true)

      // After the return animation completes (~300ms), fade it back in
      const fadeInTimeout = setTimeout(() => {
        setIsHidingForAnimation(false)
      }, 350) // Slightly longer than animation to ensure it's complete

      return () => clearTimeout(fadeInTimeout)
    } else {
      // Reset when flag is cleared
      setIsHidingForAnimation(false)
    }
  }, [isThisTabDroppingOnChat])

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition: isThisTabDroppingOnChat ? 'none' : transition,
  }

  // Track if we're dragging to prevent click events
  const [isDraggingState, setIsDraggingState] = useState(false)
  const dragStartPos = useRef<{ x: number; y: number } | null>(null)
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null)
  // Track double-click timing to prevent single click after double-click
  const [lastClickTime, setLastClickTime] = useState(0)
  const DOUBLE_CLICK_DELAY = 300 // ms

  // Create custom listeners that exclude ONLY pin/close buttons (not the tab content button)
  const customListeners = {
    ...listeners,
    onPointerDown: (e: React.PointerEvent) => {
      const target = e.target as HTMLElement

      // Ignore right-click for drag (button 2) – let context menu handle it
      if (e.button === 2) {
        console.log('[Tab Drag Debug] Right-click detected - not starting drag')
        return
      }

      // Only block drag if clicking on pin or close buttons specifically
      const isPinButton = target.closest('button[data-pin-toggle]')
      const isCloseButton = target.closest('button[data-close-tab]')
      const closestButton = target.closest('button')
      const closestButtonData = closestButton ? {
        hasDataCloseTab: closestButton.hasAttribute('data-close-tab'),
        hasDataPinToggle: closestButton.hasAttribute('data-pin-toggle'),
        className: closestButton.className,
        tagName: closestButton.tagName
      } : null

      console.log('[Tab Drag Debug] PointerDown:', {
        tabId: tab.id,
        tabType: tab.type,
        isExplorer: tab.type === 'explorer',
        tabTitle: tab.title,
        target: target.tagName,
        targetClass: target.className,
        targetId: target.id,
        isPinButton: !!isPinButton,
        isCloseButton: !!isCloseButton,
        closestButton: closestButtonData,
        closestButtonTag: closestButton?.tagName,
        pointerId: e.pointerId,
        button: e.button,
        buttons: e.buttons,
        clientX: e.clientX,
        clientY: e.clientY,
        hasListeners: !!listeners?.onPointerDown
      })

      // Store drag start position
      dragStartPos.current = { x: e.clientX, y: e.clientY }

      // Only block drag on pin/close buttons - return early to prevent drag
      if (isPinButton || isCloseButton) {
        console.log('[Tab Drag Debug] Blocking drag - clicked on pin/close button, allowing click to proceed')
        // Don't call listeners - let the button handle the click
        return
      }

      // Allow dragging from anywhere else (including tab content button)
      if (listeners?.onPointerDown) {
        console.log('[Tab Drag Debug] Calling original listener - allowing drag')
        setIsDraggingState(true)
        listeners.onPointerDown(e as any)
      } else {
        console.warn('[Tab Drag Debug] No listeners available!')
      }
    },
    onPointerMove: (e: React.PointerEvent) => {
      // Check if we've moved enough to consider it a drag
      if (dragStartPos.current) {
        const deltaX = Math.abs(e.clientX - dragStartPos.current.x)
        const deltaY = Math.abs(e.clientY - dragStartPos.current.y)
        const moved = deltaX > 5 || deltaY > 5

        if (moved && isDraggingState) {
          console.log('[Tab Drag Debug] Drag movement detected:', { deltaX, deltaY })
        }
      }

      if (listeners?.onPointerMove) {
        listeners.onPointerMove(e as any)
      }
    },
    onPointerUp: (e: React.PointerEvent) => {
      // Reset drag state
      if (dragStartPos.current) {
        const deltaX = Math.abs(e.clientX - dragStartPos.current.x)
        const deltaY = Math.abs(e.clientY - dragStartPos.current.y)
        const wasDrag = deltaX > 8 || deltaY > 8

        console.log('[Tab Drag Debug] PointerUp:', {
          tabId: tab.id,
          wasDrag,
          deltaX,
          deltaY,
          isDraggingState
        })

        if (!wasDrag) {
          setIsDraggingState(false)
        }
      }

      dragStartPos.current = null

      if (listeners?.onPointerUp) {
        listeners.onPointerUp(e as any)
      }
    }
  }

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    console.log('[Tab Drag Debug] Context menu triggered:', {
      tabId: tab.id,
      x: e.pageX,
      y: e.pageY
    })
    setContextMenu({ x: e.pageX, y: e.pageY })
  }

  const closeContextMenu = () => {
    setContextMenu(null)
  }

  return (
    <>
    <div
      ref={(node) => {
        setNodeRef(node)
        tabElementRef.current = node
      }}
      style={style}
      {...attributes}
      {...customListeners}
      onContextMenu={handleContextMenu}
      onMouseDown={(e) => {
        console.log('[Tab Drag Debug] Tab div mouseDown:', {
          tabId: tab.id,
          target: (e.target as HTMLElement).tagName,
          button: e.button,
          buttons: e.buttons
        })
      }}
      className={cn(
        "group flex items-center gap-2 px-2 py-1.5 transition-all min-w-0 flex-shrink-0 relative",
        "min-w-[80px] max-w-[180px]",
        // Chrome-like active tab (brighter, matches content background, rounded top corners, extends down)
        isActive
          ? "bg-[hsl(var(--tab-active-bg))] text-[hsl(var(--tab-active-text))] shadow-md shadow-black/5 dark:shadow-black/20 rounded-t-lg mb-[-1px] pb-[calc(0.375rem+1px)] border-b-2 border-[hsl(var(--tab-active-border))]"
          : "bg-[hsl(var(--tab-inactive-bg))] text-[hsl(var(--tab-inactive-text))] hover:bg-[hsl(var(--tab-inactive-hover-bg))] hover:text-[hsl(var(--tab-inactive-hover-text))]",
        // No pinned background overlay – use dot only
        // Hide during return animation takes priority over dragging opacity
        isHidingForAnimation
          ? "opacity-0 transition-opacity duration-0"
          : isDragging
            ? "opacity-50 z-50"
            : "opacity-100",
        "cursor-grab active:cursor-grabbing",
        // Smooth fade-in after hiding
        !isHidingForAnimation && !isDragging && "transition-all duration-200"
      )}
    >
      {/* Pinned accent dot - 4px, absolutely positioned to avoid layout shift */}
      {tab.isPinned && (
        <div
          className={cn(
            "absolute top-1 left-1.5 rounded-full z-10 pointer-events-none",
            isActive
              ? "bg-[hsl(var(--tab-pinned-active-accent))]"
              : "bg-[hsl(var(--tab-pinned-accent))]"
          )}
          style={{
            width: '4px',
            height: '4px',
            backgroundColor: isActive
              ? `hsl(var(--tab-pinned-active-accent))`
              : `hsl(var(--tab-pinned-accent))`
          }}
          title="Pinned tab"
        />
      )}

      <button
        onClick={(e) => {
          // Prevent click if we just dragged
          if (isDragging || isDraggingState) {
            console.log('[Tab Drag Debug] Tab button click prevented - was dragging')
            e.preventDefault()
            e.stopPropagation()
            return
          }

          // Check for double-click timing - if recent click, skip single click
          const now = Date.now()
          if (now - lastClickTime < DOUBLE_CLICK_DELAY) {
            console.log('[Tab Drag Debug] Tab button click skipped - double-click detected')
            return
          }
          setLastClickTime(now)

          console.log('[Tab Drag Debug] Tab button clicked:', {
            tabId: tab.id,
            isDragging,
            isDraggingState,
            defaultPrevented: e.defaultPrevented,
            timeStamp: e.timeStamp
          })
          onTabClick(tab.id)
        }}
        onDoubleClick={(e) => {
          // Double-click to toggle pin/unpin (VS Code/Cursor pattern)
          e.stopPropagation()
          e.preventDefault()
          console.log('[Tab Drag Debug] Tab button double-clicked - toggling pin:', {
            tabId: tab.id,
            currentPinned: tab.isPinned
          })
          onTabPinToggle(tab.id)
          // Reset click time to prevent single click from firing
          setLastClickTime(Date.now())
        }}
        onPointerDown={(e) => {
          // Don't prevent drag - allow pointer events to bubble up
          console.log('[Tab Drag Debug] Tab button pointerDown:', {
            tabId: tab.id,
            pointerId: e.pointerId,
            button: e.button,
            buttons: e.buttons
          })
          // Don't stop propagation - let drag handler work
        }}
        className="flex items-center min-w-0 gap-2 flex-1 pointer-events-auto pl-1"
        title={tab.isPinned ? undefined : `${tab.title} (Double-click to pin)`}
      >
        {getIcon(tab.type)}
        <span className={cn(
          "text-xs truncate flex-1 min-w-0",
          // VS Code/Cursor pattern: Unpinned tabs use italic text to indicate temporary status
          !tab.isPinned && "italic text-muted-foreground/80"
        )}>
          {tab.title}
          {tab.isDirty && <span className="text-primary ml-1">•</span>}
        </span>
      </button>

      {/* Close button */}
      <button
        data-close-tab
        onClick={(e) => {
          console.log('[Tab Drag Debug] Close button onClick FIRED:', {
            tabId: tab.id,
            tabType: tab.type,
            tabTitle: tab.title,
            isExplorer: tab.type === 'explorer',
            defaultPrevented: e.defaultPrevented,
            isDragging,
            isDraggingState,
            currentTarget: (e.currentTarget as HTMLElement).tagName,
            target: (e.target as HTMLElement).tagName,
            timeStamp: e.timeStamp,
            bubbles: e.bubbles,
            cancelable: e.cancelable
          })
          e.stopPropagation()
          e.preventDefault()
          console.log('[Tab Drag Debug] Calling onTabClose with tabId:', tab.id)
          // Force close even if drag state is set
          onTabClose(tab.id)
          console.log('[Tab Drag Debug] onTabClose call completed')
        }}
        onPointerDown={(e) => {
          console.log('[Tab Drag Debug] Close button pointerDown:', {
            tabId: tab.id,
            tabType: tab.type,
            isExplorer: tab.type === 'explorer',
            pointerId: e.pointerId,
            button: e.button,
            buttons: e.buttons,
            clientX: e.clientX,
            clientY: e.clientY,
            target: (e.target as HTMLElement).tagName,
            currentTarget: (e.currentTarget as HTMLElement).tagName
          })
          // Stop propagation to prevent drag from starting
          e.stopPropagation()
          // Don't prevent default - we want the click to work
          console.log('[Tab Drag Debug] Close button pointerDown - stopped propagation, NOT preventing default')
        }}
        onMouseDown={(e) => {
          console.log('[Tab Drag Debug] Close button mouseDown:', {
            tabId: tab.id,
            tabType: tab.type,
            isExplorer: tab.type === 'explorer',
            button: e.button,
            buttons: e.buttons,
            target: (e.target as HTMLElement).tagName
          })
          // Also stop mouse events to prevent drag
          e.stopPropagation()
          console.log('[Tab Drag Debug] Close button mouseDown - stopped propagation')
        }}
        onMouseUp={(e) => {
          console.log('[Tab Drag Debug] Close button mouseUp:', {
            tabId: tab.id,
            tabType: tab.type,
            isExplorer: tab.type === 'explorer',
            button: e.button,
            buttons: e.buttons
          })
        }}
        className="opacity-0 group-hover:opacity-100 hover:bg-muted rounded p-0.5 transition-opacity relative z-20"
        title="Close tab"
        style={{ pointerEvents: 'auto' }}
      >
        <X
          className="h-3 w-3"
          onClick={(e) => {
            console.log('[Tab Drag Debug] X icon clicked directly:', {
              tabId: tab.id,
              tabType: tab.type,
              isExplorer: tab.type === 'explorer'
            })
            e.stopPropagation()
          }}
          style={{ pointerEvents: 'none' }}
        />
      </button>
    </div>

    {/* Context Menu */}
    {contextMenu && (
      <ContextMenu
        x={contextMenu.x}
        y={contextMenu.y}
        onClose={closeContextMenu}
        context={{
          type: "tab",
          value: tab.title,
          tabId: tab.id,
          documentId: tab.id,
          isPinned: tab.isPinned,
        }}
        onTabPinToggle={onTabPinToggle}
        onTabClose={onTabClose}
      />
    )}
    </>
  )
}

// Mock chat input component with drop zone
function MockChatInput({ onDragOverChange }: { onDragOverChange?: (isOver: boolean) => void }) {
  const { setNodeRef, isOver } = useDroppable({
    id: "chat-input",
  })

  // Notify parent when drag state changes
  useEffect(() => {
    onDragOverChange?.(isOver)
  }, [isOver, onDragOverChange])

  return (
    <div
      ref={setNodeRef}
      className={cn(
        "border-t border-border p-4 bg-muted/30 transition-colors",
        isOver && "bg-primary/10 border-primary/50"
      )}
    >
      <div className="max-w-4xl mx-auto">
        <div
          className={cn(
            "border border-border rounded-lg p-4 bg-background transition-all",
            isOver && "border-primary shadow-lg ring-2 ring-primary/20"
          )}
        >
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            {isOver ? (
              <>
                <span className="text-primary font-medium">Drop tab here to add to chat</span>
              </>
            ) : (
              <>
                <span>💬</span>
                <span>Drag a tab here to add it to chat input</span>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export function TabRedesignTest() {
  const [tabs, setTabs] = useState<MockTab[]>(MOCK_TABS)
  const [activeTabId, setActiveTabId] = useState('tab-2')
  const [activeId, setActiveId] = useState<string | null>(null)
  const [isDarkMode, setIsDarkMode] = useState(false)
  const [droppedTabs, setDroppedTabs] = useState<MockTab[]>([])
  const [isDroppingOnChat, setIsDroppingOnChat] = useState(false)

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const handleDragStart = (event: DragStartEvent) => {
    const tabId = event.active.id as string
    const tab = tabs.find(t => t.id === tabId)
    console.log('[Tab Drag Debug] DragStart:', {
      tabId,
      tabTitle: tab?.title,
      activeId: event.active.id,
      data: event.active.data.current
    })
    setActiveId(tabId)
    setIsDroppingOnChat(false) // Reset on new drag
  }

  const handleDragOver = (event: DragOverEvent) => {
    // Detect when dragging over chat input to disable transition early
    // This prevents the return animation by disabling transitions before drop
    if (event.over?.id === "chat-input" && activeId) {
      if (!isDroppingOnChat) {
        console.log('[Tab Drag Debug] Dragging over chat input, disabling transition')
        setIsDroppingOnChat(true)
      }
    } else if (event.over?.id !== "chat-input") {
      // Reset when not over chat (but only if we're not in the middle of a drop)
      if (isDroppingOnChat && activeId) {
        setIsDroppingOnChat(false)
      }
    }
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    const activeTab = tabs.find(t => t.id === active.id)
    const overTab = over ? tabs.find(t => t.id === over.id) : null

    console.log('[Tab Drag Debug] DragEnd:', {
      activeId: active.id,
      activeTitle: activeTab?.title,
      overId: over?.id,
      overTitle: overTab?.title,
      delta: active.id !== over?.id ? 'different' : 'same',
      hasOver: !!over,
      isChatDrop: over?.id === "chat-input"
    })

    // Check if dropped on chat input FIRST - before any animations
    if (over?.id === "chat-input" && activeTab) {
      console.log('[Tab Drag Debug] Tab dropped on chat input:', activeTab.title)
      // Set flag immediately to disable transition and reset transform
      setIsDroppingOnChat(true)
      // Clear activeId immediately to hide drag overlay
      setActiveId(null)
      setDroppedTabs(prev => [...prev, activeTab])
      // Reset flag after animation would have completed
      // This prevents the return animation from being visible
      setTimeout(() => {
        setIsDroppingOnChat(false)
      }, 200)
      // Return early to prevent reordering logic
      return
    }

    // Reset flag if not dropping on chat
    setIsDroppingOnChat(false)

    // For normal drag operations, clear activeId normally
    setActiveId(null)

    // Small delay to ensure drag state is cleared before allowing clicks
    setTimeout(() => {
      console.log('[Tab Drag Debug] Drag state cleared')
    }, 100)

    // Handle tab reordering
    if (active.id !== over?.id && over) {
      const oldIndex = tabs.findIndex((tab) => tab.id === active.id)
      const newIndex = tabs.findIndex((tab) => tab.id === over.id)

      console.log('[Tab Drag Debug] Reordering:', {
        oldIndex,
        newIndex,
        from: activeTab?.title,
        to: overTab?.title
      })

      const reorderedTabs = arrayMove(tabs, oldIndex, newIndex)
      setTabs(reorderedTabs)
    } else {
      console.log('[Tab Drag Debug] No reorder - same position or no drop target')
    }
  }

  const handleTabClick = (tabId: string) => {
    setActiveTabId(tabId)
  }

  const handleTabClose = (tabId: string) => {
    console.log('[Tab Drag Debug] handleTabClose called:', {
      tabId,
      currentTabs: tabs.map(t => t.id),
      activeTabId
    })

    const newTabs = tabs.filter(t => t.id !== tabId)
    console.log('[Tab Drag Debug] New tabs after close:', newTabs.map(t => t.id))

    setTabs(newTabs)

    if (activeTabId === tabId) {
      const newActiveId = newTabs.length > 0 ? newTabs[0].id : null
      console.log('[Tab Drag Debug] Setting new active tab:', newActiveId)
      setActiveTabId(newActiveId || '')
    }
  }

  const handleTabPinToggle = (tabId: string) => {
    setTabs(tabs.map(t =>
      t.id === tabId ? { ...t, isPinned: !t.isPinned } : t
    ))
  }

  const activeTab = tabs.find(t => t.id === activeTabId)
  const activeTabForDrag = activeId ? tabs.find(t => t.id === activeId) : null

  // Toggle dark mode
  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode)
    document.documentElement.classList.toggle('dark')
  }

  return (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
        >
      <div className={cn("h-screen flex flex-col", isDarkMode ? "dark" : "")}>
        {/* Header Controls */}
        <div className="border-b p-4 bg-sidebar flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold">Tab Redesign Prototype</h1>
            <p className="text-sm text-muted-foreground">Chrome-like tab interface with improved visuals</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.location.href = '/?test=index'}
            >
              ← Back to Index
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={toggleDarkMode}
            >
              {isDarkMode ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>
          </div>
        </div>

        {/* Tab Bar - Chrome-like styling */}
        <div
          className="relative flex items-end border-b min-h-[40px] w-full"
          style={{
            backgroundColor: 'hsl(var(--tab-bar-bg))',
            borderColor: 'hsl(var(--tab-bar-border))',
          }}
        >
          <SortableContext
            items={tabs.map(tab => tab.id)}
            strategy={horizontalListSortingStrategy}
          >
            <div className="flex items-end gap-0.5 px-1 flex-nowrap w-full overflow-x-auto">
              {tabs.map((tab) => (
                <SortableTabPrototype
                  key={tab.id}
                  tab={tab}
                  isActive={activeTabId === tab.id}
                  onTabClick={handleTabClick}
                  onTabClose={handleTabClose}
                  onTabPinToggle={handleTabPinToggle}
                  isDragging={activeId === tab.id}
                  isDroppingOnChat={isDroppingOnChat}
                  activeTabId={activeId}
                />
              ))}
            </div>
          </SortableContext>

          <DragOverlay>
            {activeTabForDrag ? (
              <div
                className={cn(
                  "group flex items-center gap-2 px-2 py-1.5 transition-colors min-w-0 flex-shrink-0 relative",
                  "min-w-[80px] max-w-[180px]",
                  "bg-[hsl(var(--tab-active-bg))] text-[hsl(var(--tab-active-text))] shadow-md shadow-black/5 dark:shadow-black/20 rounded-t-lg border-b-2 border-[hsl(var(--tab-active-border))]"
                )}
              >
                {getIcon(activeTabForDrag.type)}
                <span className={cn(
                  "text-xs truncate flex-1 min-w-0",
                  // Show italic for unpinned tabs in drag overlay too
                  !activeTabForDrag.isPinned && "italic text-muted-foreground/80"
                )}>
                  {activeTabForDrag.title}
                  {activeTabForDrag.isDirty && <span className="text-primary ml-1">•</span>}
                </span>
                {activeTabForDrag.isPinned && (
                  <div
                    className="absolute top-1 left-1 rounded-full z-10 bg-[hsl(var(--tab-pinned-active-accent))]"
                    style={{ width: '4px', height: '4px' }}
                    title="Pinned tab"
                  />
                )}
              </div>
            ) : null}
          </DragOverlay>

        </div>

      {/* Document Content Area - Connected to active tab */}
      <div
        className="flex-1 min-h-0 flex flex-col relative overflow-auto p-8 border-t border-transparent"
        style={{
          backgroundColor: 'hsl(var(--tab-active-bg))',
        }}
      >
        <div className="max-w-4xl mx-auto w-full">
          <h2 className="text-2xl font-bold mb-4">
            {activeTab?.title || 'No tab selected'}
          </h2>

          <div className="prose dark:prose-invert max-w-none">
            <p className="text-muted-foreground mb-4">
              This is the document content area. Notice how the active tab flows seamlessly into this content -
              there's no border separator, creating a Chrome-like visual connection.
            </p>

            <div className="border border-border rounded-lg p-6 bg-card mb-6">
              <h3 className="text-lg font-semibold mb-3">Design Features</h3>
              <ul className="space-y-2">
                <li>✅ <strong>Tab bar background</strong> is darker than content (creates depth)</li>
                <li>✅ <strong>Active tab</strong> matches content background (seamless connection)</li>
                <li>✅ <strong>No border separator</strong> between active tab and content</li>
                <li>✅ <strong>Pinned tabs</strong> show accent dot indicator (top-right)</li>
                <li>✅ <strong>Whole tab is draggable</strong> (try dragging any tab!)</li>
                <li>✅ <strong>Hover states</strong> provide clear feedback</li>
              </ul>
            </div>

            <div className="border border-border rounded-lg p-6 bg-card mb-6">
              <h3 className="text-lg font-semibold mb-3">Try These Interactions</h3>
              <ul className="space-y-2">
                <li>🖱️ <strong>Click tabs</strong> to switch between documents</li>
                <li>📌 <strong>Hover over tabs</strong> to see pin/unpin button</li>
                <li>📌 <strong>Click pin icon</strong> to pin/unpin tabs</li>
                <li>🖱️ <strong>Drag tabs</strong> to reorder them</li>
                <li>❌ <strong>Hover and click X</strong> to close tabs</li>
                <li>🌓 <strong>Toggle dark mode</strong> to see color system in action</li>
              </ul>
            </div>

            <div className="border border-border rounded-lg p-6 bg-card">
              <h3 className="text-lg font-semibold mb-3">Tab States</h3>
              <div className="space-y-2 text-sm">
                <div>
                  <strong>Active Tab:</strong> Bright background matching content, high contrast text
                </div>
                <div>
                  <strong>Inactive Tabs:</strong> Intermediate brightness, lower contrast
                </div>
                <div>
                  <strong>Pinned Tabs:</strong> Accent dot indicator + subtle background tint
                </div>
                <div>
                  <strong>Hover:</strong> Slightly brighter background, text becomes more prominent
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Dropped Tabs Feedback */}
      {droppedTabs.length > 0 && (
        <div className="border-t p-4 bg-muted/30">
          <div className="max-w-4xl mx-auto">
            <h3 className="text-sm font-semibold mb-2">Tabs Added to Chat:</h3>
            <div className="flex flex-wrap gap-2">
              {droppedTabs.map((tab, idx) => (
                <div
                  key={`${tab.id}-${idx}`}
                  className="px-3 py-1.5 bg-primary/10 border border-primary/20 rounded-md text-sm flex items-center gap-2"
                >
                  {getIcon(tab.type)}
                  <span>{tab.title}</span>
                  {tab.isPinned && (
                    <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                  )}
                </div>
              ))}
            </div>
            <button
              onClick={() => setDroppedTabs([])}
              className="mt-2 text-xs text-muted-foreground hover:text-foreground"
            >
              Clear list
            </button>
          </div>
        </div>
      )}

      {/* CSS Variables Info */}
      <div className="border-t p-4 bg-muted/30 text-xs">
        <div className="max-w-4xl mx-auto">
          <strong>CSS Variables Used:</strong> All colors are controlled via CSS variables in <code>index.css</code>:
          <code className="block mt-1 p-2 bg-background rounded border">
            --tab-bar-bg, --tab-active-bg, --tab-inactive-bg, --tab-pinned-accent, etc.
          </code>
        </div>
      </div>

      {/* Mock Chat Input with Drop Zone - Must be inside DndContext */}
      <MockChatInput />
    </div>
    </DndContext>
  )
}

